export const MODULES = {
  finance: { name: "Finance & Accounting", problem: "Books scattered across Excel files and registers", benefit: "One live view of income, expenses and cash position" },
  expenses: { name: "Expense Management", problem: "Receipts in WhatsApp, reimbursements delayed, leakage invisible", benefit: "Capture, approve and control every expense by center and category" },
  invoices: { name: "Invoice Management", problem: "Follow-ups forgotten, overdue payments untracked", benefit: "GST-ready invoices with status tracking and overdue lists" },
  reports: { name: "Reports & Analytics", problem: "Month-end reporting takes days of manual stitching", benefit: "Real-time dashboards and exportable reports" },
  employees: { name: "Employee Management", problem: "Employee data spread across files and folders", benefit: "One organized record per employee, searchable and secure" },
  attendance: { name: "Attendance", problem: "Registers and chat messages cause payroll disputes", benefit: "Center-wise attendance that flows directly into payroll" },
  payroll: { name: "Payroll", problem: "Manual salary calculation is slow and error-prone", benefit: "Automated, attendance-linked payroll runs in minutes" },
  approvals: { name: "Approval Workflow", problem: "Approvals lost in chat with no audit trail", benefit: "Recorded approvals with full accountability" },
  organization: { name: "Organization Management", problem: "No single view of the whole organization", benefit: "Structure, roles and governance in one place" },
  multi_center: { name: "Multi-Center Management", problem: "Every branch runs its own disconnected system", benefit: "Center-wise operations with consolidated head-office visibility" },
  partner_settlement: { name: "Partner Settlement", problem: "Partner payouts calculated manually and disputed later", benefit: "Transparent, recorded partner settlements with a clear audit trail" },
  candidates: { name: "Candidate Management", problem: "Candidate pipelines tracked in scattered spreadsheets", benefit: "One organized candidate database connected to your operations" },
};

export const MODULE_LINKS = {
  finance: "/finance-management-software",
  expenses: "/expense-management-software",
  invoices: "/invoice-management-software",
  reports: "/business-management-software",
  employees: "/employee-management-software",
  attendance: "/attendance-management-software",
  payroll: "/payroll-software",
  approvals: "/hr-payroll-software",
  organization: "/business-management-software",
  multi_center: "/multi-branch-management-software",
};

export const PROBLEMS = [
  "Multiple Excel files for finance",
  "Manual payroll calculation",
  "Attendance scattered across registers and apps",
  "Expense receipts lost in WhatsApp chats",
  "Invoice follow-ups forgotten",
  "Approvals stuck in message threads",
  "Reports take days to prepare",
  "Employee data spread across folders",
  "No real-time visibility for leadership",
  "Disconnected tools that don't talk to each other",
];

export const BENEFITS = [
  { title: "Save Time", desc: "Automate payroll, invoicing and reporting instead of re-entering data across sheets." },
  { title: "Reduce Errors", desc: "Attendance-linked payroll and single-entry records remove manual calculation mistakes." },
  { title: "Improve Financial Visibility", desc: "Live dashboards show income, expenses and cash position across every center." },
  { title: "Automate Payroll", desc: "Salary structures plus attendance data produce accurate payroll runs in minutes." },
  { title: "Track Expenses", desc: "Capture at source, categorize, approve and review spending by center." },
  { title: "Control Approvals", desc: "Every approval is recorded — who, what, when — with a complete audit trail." },
  { title: "Monitor Employees", desc: "Employee records, attendance and roles organized in one secure place." },
  { title: "Generate Reports", desc: "Instant, exportable reports replace the month-end spreadsheet ritual." },
  { title: "Manage Multiple Locations", desc: "Center-wise operations with consolidated head-office visibility." },
  { title: "Improve Accountability", desc: "Role-based access and recorded actions make ownership clear." },
];

export const BEFORE_AFTER = {
  before: ["Excel spreadsheets", "WhatsApp approvals", "Manual attendance registers", "Manual payroll calculation", "Scattered expense receipts", "Manual invoice tracking", "Reports delayed by days"],
  after: ["Centralized business data", "Automated approval workflows", "Digital center-wise attendance", "Automated payroll runs", "Controlled expense management", "Invoice status tracking", "Real-time reports & dashboards"],
};

export const SEGMENTS = [
  { slug: "training-organizations", icon: "GraduationCap", title: "Training Organizations", desc: "Manage centers, batches, trainers, attendance, payroll, expenses, invoices and reports from one platform.", points: ["Center & batch management", "Trainer payroll linked to attendance", "Center-wise expense control"] },
  { slug: "skill-development-organizations", icon: "Target", title: "Skill Development Organizations", desc: "Run multi-center skill programs with batch tracking, staff management and transparent financial reporting.", points: ["Program & batch oversight", "Multi-center consolidation", "Audit-ready reports"] },
  { slug: "ngos", icon: "HeartHandshake", title: "NGOs", desc: "Bring discipline to program expenses, staff payroll and donor-ready financial reporting.", points: ["Program expense tracking", "Approval workflows", "Transparent reporting"] },
  { slug: "small-businesses", icon: "Store", title: "Small Businesses", desc: "Replace Excel and WhatsApp with one system for finance, employees, attendance and invoicing.", points: ["Quick setup in a day", "Affordable starter plan", "Everything in one login"] },
  { slug: "growing-companies", icon: "TrendingUp", title: "Growing Companies", desc: "Scale from 5 to 50+ employees without losing control of payroll, expenses and approvals.", points: ["Role-based access", "Approval workflows", "Real-time dashboards"] },
  { slug: "educational-institutes", icon: "School", title: "Educational Institutes", desc: "Manage staff, attendance, payroll, fees invoicing and center-wise expenses with confidence.", points: ["Staff attendance & payroll", "Invoice management", "Multi-branch ready"] },
  { slug: "multi-center-organizations", icon: "Building2", title: "Multi-Center Organizations", desc: "Standardize operations across locations with center-wise data and head-office visibility.", points: ["Up to 10 centers", "Center-wise P&L views", "Central role control"] },
  { slug: "hr-finance-teams", icon: "Users", title: "HR & Finance Teams", desc: "Give HR and finance one shared source of truth — from attendance to payroll to reports.", points: ["Shared employee records", "Attendance-to-payroll flow", "Exportable reports"] },
];

export const LANDING_PAGES = {
  "payroll-software": {
    keyword: "Payroll Software",
    meta_title: "Payroll Software India — Attendance-Linked Salary Processing | Mashara Finance",
    meta_description: "Run accurate, auditable payroll in minutes with Mashara Finance. Salary structures, attendance-linked payable days and centre-wise registers in one platform.",
    h1: "Payroll Software That Runs Salary in Minutes, Not Days",
    sub: "Attendance-linked, automated payroll for Indian businesses — no more spreadsheet formulas or salary disputes.",
    problems_heading: "Payroll Problems Most Teams Still Face",
    features_heading: "How Mashara Finance Automates Your Payroll",
    faq_heading: "Payroll Software FAQs",
    mockup: "payroll",
    alt: "Mashara Finance payroll register screen showing employee salaries, payable days and a one-click payroll run",
    problems: ["Manual salary calculation every month", "Attendance mismatches causing disputes", "No audit trail for payroll changes", "Days spent preparing salary sheets"],
    features: ["payroll", "attendance", "employees", "reports"],
    related: [
      { label: "Attendance Management Software", to: "/attendance-management-software" },
      { label: "HR & Payroll Software", to: "/hr-payroll-software" },
      { label: "Employee Management Software", to: "/employee-management-software" },
      { label: "Guide: How to Automate Payroll", to: "/blog/how-to-automate-payroll" },
    ],
    faqs: [
      { q: "How does automated payroll work?", a: "Define salary structures once, record attendance, and run payroll in one click. Payable days are computed directly from attendance." },
      { q: "Is there a free trial?", a: "Yes — a 10-day trial of the full platform with a ₹1 verification payment. No subscription is charged during the trial." },
      { q: "Can it handle multiple centers?", a: "Yes, center-wise attendance and payroll with consolidated head-office reporting." },
    ],
  },
  "expense-management-software": {
    keyword: "Expense Management Software",
    meta_title: "Expense Management Software with Approval Workflows | Mashara Finance",
    meta_description: "Capture, approve and track every business expense by category and centre. Approval workflows, real-time spend reports and head-office control in one place.",
    h1: "Expense Management Software That Stops Leakage",
    sub: "Capture, approve and control every business expense — by category, by center, in real time.",
    problems_heading: "Where Business Expenses Go Untracked",
    features_heading: "Expense Control With Mashara Finance",
    faq_heading: "Expense Management FAQs",
    mockup: "expenses",
    alt: "Mashara Finance expense management screen listing categorized expenses with approval status by centre",
    problems: ["Receipts lost in WhatsApp chats", "No approval trail for spending", "Month-end expense surprises", "No per-center visibility"],
    features: ["expenses", "approvals", "reports", "multi_center"],
    related: [
      { label: "Finance Management Software", to: "/finance-management-software" },
      { label: "Invoice Management Software", to: "/invoice-management-software" },
      { label: "Solutions for NGOs", to: "/solutions/ngos" },
      { label: "Guide: How to Control Business Expenses", to: "/blog/how-to-control-business-expenses" },
    ],
    faqs: [
      { q: "Can employees submit expenses themselves?", a: "Yes — expenses are captured at source with category and amount, then routed through approvals." },
      { q: "Does it support multiple branches?", a: "Yes, expense tracking and reporting is center-wise with consolidated views." },
      { q: "Is there a free trial?", a: "Yes — 10 days of the full platform with a ₹1 verification payment." },
    ],
  },
  "finance-management-software": {
    keyword: "Finance Management Software",
    meta_title: "Finance Management Software for Growing Organizations | Mashara Finance",
    meta_description: "Replace scattered Excel files with one connected finance platform: accounting, expenses, invoices and a live cash dashboard for growing Indian organizations.",
    h1: "Finance Management Software for Growing Organizations",
    sub: "Replace scattered Excel files with one live view of income, expenses, invoices and cash position.",
    problems_heading: "Why Excel-Based Finance Breaks as You Grow",
    features_heading: "One Connected Finance System",
    faq_heading: "Finance Software FAQs",
    mockup: "finance",
    alt: "Mashara Finance accounting dashboard showing income, expenses, cash position and recent transactions",
    problems: ["Multiple versions of the same spreadsheet", "No real-time cash visibility", "Reports take days to prepare", "Disconnected invoicing and expenses"],
    features: ["finance", "expenses", "invoices", "reports"],
    related: [
      { label: "Expense Management Software", to: "/expense-management-software" },
      { label: "Invoice Management Software", to: "/invoice-management-software" },
      { label: "Business Management Software", to: "/business-management-software" },
      { label: "Guide: Excel vs Finance Management Software", to: "/blog/excel-vs-finance-management-software" },
    ],
    faqs: [
      { q: "Can I export my financial data?", a: "Yes — reports and records are exportable anytime. Your data remains yours." },
      { q: "Is it suitable for small businesses?", a: "Yes — the Starter plan covers finance, expenses, invoices and reports for up to 5 users." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "attendance-management-software": {
    keyword: "Attendance Management Software",
    meta_title: "Attendance Management Software Linked to Payroll | Mashara Finance",
    meta_description: "Centre-wise digital attendance that flows straight into payroll. Replace registers and chat check-ins with real-time head-office visibility across locations.",
    h1: "Attendance Management Software Connected to Payroll",
    sub: "Center-wise digital attendance that flows straight into payroll — no registers, no disputes.",
    problems_heading: "The Cost of Manual Attendance Tracking",
    features_heading: "Attendance That Feeds Payroll Automatically",
    faq_heading: "Attendance Software FAQs",
    mockup: "attendance",
    alt: "Mashara Finance attendance screen with centre-wise daily presence, leave records and payroll-ready payable days",
    problems: ["Paper registers and chat-based attendance", "Payroll disputes over payable days", "No head-office visibility across centers", "Manual leave tracking"],
    features: ["attendance", "payroll", "employees", "multi_center"],
    related: [
      { label: "Payroll Software", to: "/payroll-software" },
      { label: "Employee Management Software", to: "/employee-management-software" },
      { label: "Multi-Branch Management Software", to: "/multi-branch-management-software" },
      { label: "Guide: How to Manage Employee Attendance", to: "/blog/how-to-manage-employee-attendance" },
    ],
    faqs: [
      { q: "Does attendance connect to payroll?", a: "Yes — payable days are computed directly from recorded attendance, removing manual errors." },
      { q: "Can each center record attendance separately?", a: "Yes — every center records in the same system and head office sees consolidated data." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "employee-management-software": {
    keyword: "Employee Management Software",
    meta_title: "Employee Management Software with HRMS Records & RBAC | Mashara Finance",
    meta_description: "Keep one organized, secure record per employee — roles, salary structure, attendance history and documents — with role-based access control for every team.",
    h1: "Employee Management Software With HR, Attendance & Payroll in One Place",
    sub: "One organized record per employee — connected to attendance, payroll and role-based access.",
    problems_heading: "When Employee Data Lives in Folders",
    features_heading: "A Single Source of Truth for Your People",
    faq_heading: "Employee Management FAQs",
    mockup: "hr",
    alt: "Mashara Finance HRMS employee directory showing employee records, roles, departments and centre assignments",
    problems: ["Employee data scattered across folders", "No single source of truth for HR", "Onboarding tracked in notebooks", "Roles and permissions unmanaged"],
    features: ["employees", "attendance", "payroll", "organization"],
    related: [
      { label: "HR & Payroll Software", to: "/hr-payroll-software" },
      { label: "Attendance Management Software", to: "/attendance-management-software" },
      { label: "Solutions for Educational Institutes", to: "/solutions/educational-institutes" },
      { label: "Guide: How to Reduce Payroll Errors", to: "/blog/how-to-reduce-payroll-errors" },
    ],
    faqs: [
      { q: "What employee data can I store?", a: "Complete employee records with roles, salary structures and attendance history." },
      { q: "Can I control who sees employee data?", a: "Yes — role-based access controls every module." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "invoice-management-software": {
    keyword: "Invoice Management Software",
    meta_title: "Invoice Management Software — GST-Ready Billing & Tracking | Mashara Finance",
    meta_description: "Create GST-ready invoices, track payment status and never miss a follow-up. Overdue lists and status tracking keep your receivables and cash flow under control.",
    h1: "Invoice Management Software That Protects Your Cash Flow",
    sub: "Create GST-ready invoices, track statuses and never miss a payment follow-up again.",
    problems_heading: "Why Receivables Slip Through the Cracks",
    features_heading: "Systematic Invoicing With Mashara Finance",
    faq_heading: "Invoice Software FAQs",
    mockup: "invoices",
    alt: "Mashara Finance invoice management screen showing GST-ready invoices with paid, pending and overdue status",
    problems: ["Invoices created in Word/Excel templates", "Overdue payments forgotten", "No single list of what's pending", "Follow-ups depend on memory"],
    features: ["invoices", "finance", "reports", "approvals"],
    related: [
      { label: "Finance Management Software", to: "/finance-management-software" },
      { label: "Expense Management Software", to: "/expense-management-software" },
      { label: "Solutions for Small Businesses", to: "/solutions/small-businesses" },
      { label: "Guide: How to Track Business Invoices", to: "/blog/how-to-track-business-invoices" },
    ],
    faqs: [
      { q: "Are invoices GST-ready?", a: "Yes — invoices are GST-ready with sequential numbering and records." },
      { q: "Can I track overdue invoices?", a: "Yes — status tracking and overdue views make follow-ups systematic." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "business-management-software": {
    keyword: "Business Management Software",
    meta_title: "All-in-One Business Management Software for India | Mashara Finance",
    meta_description: "Run finance, accounting, HRMS, payroll, attendance, expenses, invoices and operations from one connected platform instead of five disconnected tools and Excel.",
    h1: "Business Management Software: Finance, HR, Payroll & More — One Platform",
    sub: "Stop paying for five disconnected tools. Run your entire operation from one connected system.",
    problems_heading: "The Hidden Cost of Disconnected Tools",
    features_heading: "Every Core Business Module, Connected",
    faq_heading: "Business Management Software FAQs",
    mockup: "dashboard",
    alt: "Mashara Finance business operations dashboard with finance, HR, payroll and attendance summaries across centres",
    problems: ["Five tools that don't talk to each other", "Data re-entered everywhere", "No unified view of the business", "Rising software subscription costs"],
    features: ["finance", "employees", "payroll", "reports"],
    related: [
      { label: "Finance Management Software", to: "/finance-management-software" },
      { label: "HR & Payroll Software", to: "/hr-payroll-software" },
      { label: "Multi-Branch Management Software", to: "/multi-branch-management-software" },
      { label: "Guide: Business Finance Management Checklist", to: "/blog/business-finance-management-checklist" },
    ],
    faqs: [
      { q: "What modules are included?", a: "Finance, expenses, invoices, employees, attendance, payroll, approvals, reports and multi-center management." },
      { q: "Can I start with a few modules?", a: "Yes — the Starter plan includes core finance modules; upgrade anytime." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "multi-branch-management-software": {
    keyword: "Multi Branch Management Software",
    meta_title: "Multi-Branch Management Software with Head-Office Control | Mashara Finance",
    meta_description: "Standardize finance, HR, attendance and payroll across every branch. Centre-wise data, consolidated head-office dashboards and central role control in one tool.",
    h1: "Multi-Branch Management Software With Head-Office Visibility",
    sub: "Standardize operations across every branch — center-wise data, consolidated reports, central control.",
    problems_heading: "Why Multi-Branch Operations Drift Apart",
    features_heading: "Central Control, Local Execution",
    faq_heading: "Multi-Branch Software FAQs",
    mockup: "reports",
    alt: "Mashara Finance consolidated reports screen comparing centre-wise profit and loss, payroll and expenses",
    problems: ["Every branch runs its own system", "Consolidation takes days", "No real-time branch comparison", "Inconsistent processes across locations"],
    features: ["multi_center", "organization", "reports", "finance"],
    related: [
      { label: "Solutions for Multi-Center Organizations", to: "/solutions/multi-center-organizations" },
      { label: "Attendance Management Software", to: "/attendance-management-software" },
      { label: "Expense Management Software", to: "/expense-management-software" },
      { label: "Guide: How to Manage Multiple Centers", to: "/blog/how-to-manage-multiple-centers" },
    ],
    faqs: [
      { q: "How many branches are supported?", a: "Professional supports up to 3 centers; Business supports up to 10 with consolidated reporting." },
      { q: "Can branch managers have limited access?", a: "Yes — role-based access lets branches manage their operations while head office sees everything." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
  "hr-payroll-software": {
    keyword: "HR & Payroll Software",
    meta_title: "HR & Payroll Software for Indian Organizations | Mashara Finance",
    meta_description: "HRMS and payroll in one data flow: employee records, attendance and approvals drive accurate salary processing with role-based access for HR and accounts teams.",
    h1: "HR & Payroll Software Built for Indian Organizations",
    sub: "Employee records, attendance and payroll in one flow — accurate, auditable and fast.",
    problems_heading: "When HR and Payroll Don't Talk to Each Other",
    features_heading: "HRMS and Payroll in a Single Flow",
    faq_heading: "HR & Payroll Software FAQs",
    mockup: "hr",
    alt: "Mashara Finance HRMS screen showing employee records connected to attendance, approvals and payroll processing",
    problems: ["HR data separate from payroll", "Manual salary computation", "Leave and attendance disputes", "No employee self-visibility"],
    features: ["employees", "attendance", "payroll", "approvals"],
    related: [
      { label: "Payroll Software", to: "/payroll-software" },
      { label: "Employee Management Software", to: "/employee-management-software" },
      { label: "Solutions for Training Organizations", to: "/solutions/training-organizations" },
      { label: "Guide: How to Reduce Payroll Errors", to: "/blog/how-to-reduce-payroll-errors" },
    ],
    faqs: [
      { q: "Does it combine HR and payroll?", a: "Yes — employee records, attendance and payroll share one data flow." },
      { q: "Is it suitable for small teams?", a: "Yes — plans start at 5 users and scale to unlimited." },
      { q: "Is there a free trial?", a: "Yes — 10 days with a ₹1 verification payment." },
    ],
  },
};

export const LP_PAGES = {
  "payroll-software": "payroll-software",
  "expense-management": "expense-management-software",
  "finance-software": "finance-management-software",
  "attendance-software": "attendance-management-software",
};

export const SOLUTION_PAGES = {
  "training-organizations": {
    title: "Mashara Finance for Training Organizations",
    h1: "Run Every Training Center From One Platform",
    sub: "Centers, batches, trainers, attendance, payroll, expenses, invoices and reports — purpose-built for training operations.",
    manages: ["Centers & batches", "Trainers & employees", "Center-wise attendance", "Trainer payroll", "Program expenses", "Student/client invoices", "Consolidated reports"],
    points: ["Batch and center management built in", "Attendance-linked trainer payroll eliminates disputes", "Center-wise expense control with head-office visibility", "Audit-ready financial reports for management and funders"],
  },
  "ngos": {
    title: "Mashara Finance for NGOs",
    h1: "Financial Discipline and Transparency for Your NGO",
    sub: "Track program expenses, run staff payroll and produce transparent reports — without an accounts department.",
    manages: ["Program & center expenses", "Staff records & attendance", "Payroll processing", "Approval workflows", "Vendor invoices", "Donor-ready reports"],
    points: ["Approval workflows create accountability for every rupee", "Program-wise expense tracking for transparent reporting", "Simple enough for non-accountants to run", "Exportable, audit-friendly records"],
  },
  "small-businesses": {
    title: "Mashara Finance for Small Businesses",
    h1: "Everything Your Small Business Needs. One Login.",
    sub: "Finance, employees, attendance, payroll and invoicing — set up in a day, priced for small teams.",
    manages: ["Income & expenses", "Employee records", "Attendance", "Payroll", "GST-ready invoices", "Business reports"],
    points: ["Replace Excel and WhatsApp in one move", "Affordable Starter plan for up to 5 users", "Guided onboarding checklist gets you live fast", "Upgrade seamlessly as you grow"],
  },
  "educational-institutes": {
    title: "Mashara Finance for Educational Institutes",
    h1: "Staff, Payroll, Fees & Expenses — Managed in One Place",
    sub: "From staff attendance to payroll to invoice tracking, run your institute's back office with confidence.",
    manages: ["Teaching & non-teaching staff", "Staff attendance", "Salary processing", "Fee & vendor invoices", "Center-wise expenses", "Management reports"],
    points: ["Staff attendance linked directly to salary", "Multi-branch ready as your institute grows", "Role-based access for admin, accounts and management", "Instant reports for trust and board reviews"],
  },
  "multi-center-organizations": {
    title: "Mashara Finance for Multi-Center Organizations",
    meta_title: "Multi-Center Organization Software — One Head-Office View | Mashara Finance",
    meta_description: "Manage up to 10 centres from one platform: centre-wise employees, attendance, payroll and expenses with consolidated dashboards and central role management.",
    manages_heading: "What Head Office and Every Center Manage Together",
    points_heading: "Why Multi-Center Organizations Choose Mashara Finance",
    alt: "Mashara Finance head-office dashboard consolidating finance, attendance and payroll data from multiple centres",
    related: [
      { label: "Multi-Branch Management Software", to: "/multi-branch-management-software" },
      { label: "Attendance Management Software", to: "/attendance-management-software" },
      { label: "Payroll Software", to: "/payroll-software" },
      { label: "Guide: How to Manage Multiple Centers", to: "/blog/how-to-manage-multiple-centers" },
    ],
    h1: "One Head-Office View Across All Your Centers",
    sub: "Standardize finance, HR, attendance and payroll across locations — with center-wise data and central control.",
    manages: ["Up to 10 centers", "Center-wise employees", "Center-wise attendance & payroll", "Inter-center expense comparison", "Consolidated dashboards", "Central role management"],
    points: ["Consolidated real-time dashboard across centers", "Center managers run local operations with role-based access", "Compare expense and payroll efficiency across locations", "Standard processes, enforced by the platform"],
  },
};

export const FAQ_PAGE_LINK = "/faq";
